import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Calendar, Star } from "lucide-react";
import type { JournalEntry } from "@shared/schema";

interface GoalJournalTabProps {
  goalId: string;
}

export default function GoalJournalTab({ goalId }: GoalJournalTabProps) {
  const [, setLocation] = useLocation();

  const { data: entries = [], isLoading } = useQuery<JournalEntry[]>({
    queryKey: ["/api/journal", goalId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (goalId) params.append("goalId", goalId);
      const response = await fetch(`/api/journal?${params.toString()}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error('Failed to fetch journal entries');
      return response.json();
    },
  });

  const handleCreateEntry = () => {
    setLocation(`/journal?goalId=${goalId}`);
  };

  const handleViewEntry = (entryId: string) => {
    // Navigate to journal page with the specific entry
    setLocation(`/journal?entryId=${entryId}`);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getExcerpt = (plainContent: string, maxLength: number = 150) => {
    if (plainContent.length <= maxLength) return plainContent;
    return plainContent.substring(0, maxLength).trim() + "...";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Journal Entries</h2>
          <p className="text-sm text-muted-foreground">Journal entries linked to this goal</p>
        </div>
        <Button onClick={handleCreateEntry} data-testid="button-create-journal">
          <Plus className="w-4 h-4 mr-2" />
          New Entry
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8">
          <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading journal entries...</p>
        </div>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">No journal entries linked to this goal yet</p>
            <Button 
              variant="outline" 
              onClick={handleCreateEntry}
              data-testid="button-create-first-journal"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Entry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {entries.map((entry) => (
            <Card 
              key={entry.id} 
              data-testid={`card-journal-${entry.id}`}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleViewEntry(entry.id)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {entry.isPinned && <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />}
                      {entry.title}
                    </CardTitle>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        {formatDate(entry.createdAt)}
                      </div>
                      {entry.status === "draft" && (
                        <Badge variant="outline">Draft</Badge>
                      )}
                      {entry.mood && (
                        <Badge variant="outline">{entry.mood}</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {getExcerpt(entry.plainContent)}
                </p>
                {entry.tags && entry.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {entry.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
